/**
 * Build-time configuration (ui-design §6.3). A single environment today, so the
 * Auth0 values are Vite env vars; a runtime `/config.js` can come with a second
 * environment.
 */

export type AuthMode = 'dev' | 'auth0';

const rawMode = import.meta.env.VITE_AUTH_MODE;

export const AUTH_MODE: AuthMode = rawMode === 'auth0' ? 'auth0' : 'dev';

/** local-dev.md §2.4: the seeded office user the dev guard falls back to. */
export const DEFAULT_DEV_USER = import.meta.env.VITE_DEV_USER ?? 'lew@puntabrava.com';

export const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN ?? '';
export const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID ?? '';
/**
 * The API audience — a STATIC, opaque identifier, and deliberately **not** a URL
 * anything serves (it does not track the app's origin, and a hostname change no
 * longer moves it). It is declared once, in
 * `deployment/terraform/locals.tf` as `auth0_api_identifier`, and flows from
 * there to the Auth0 resource server, the server's `AUTH0_AUDIENCE` and the
 * `VITE_AUTH0_AUDIENCE` CI publishes for this build — so all of them are the
 * same string by construction.
 */
export const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE ?? '';

/**
 * Google is the only connection in the tenant
 * (`deployment/terraform/modules/auth0/google.tf`, `auth0_connection.ni_google`).
 * Naming it on the authorize call skips Auth0's intermediate "choose a login
 * method" screen and goes straight to Google, which is the whole experience for
 * a one-connection tenant.
 *
 * Not an env var: there is one tenant and one connection name, so a rename is a
 * Terraform edit and a code edit, never a per-environment build flag.
 */
export const AUTH0_CONNECTION = 'ni-google';

/**
 * A build that asks for Auth0 without the configuration cannot sign anyone in,
 * and the failure it would otherwise produce is a redirect to a nonexistent
 * tenant. Say so at boot instead.
 */
if (AUTH_MODE === 'auth0') {
  const missing = [
    AUTH0_DOMAIN ? null : 'VITE_AUTH0_DOMAIN',
    AUTH0_CLIENT_ID ? null : 'VITE_AUTH0_CLIENT_ID',
    AUTH0_AUDIENCE ? null : 'VITE_AUTH0_AUDIENCE',
  ].filter((name): name is string => name !== null);

  if (missing.length > 0) {
    throw new Error(
      `VITE_AUTH_MODE=auth0 but this build is missing ${missing.join(', ')}. ` +
        'Set them at build time, or build with VITE_AUTH_MODE=dev against a local server.',
    );
  }
}
